// run-command-prompt-decided-questions-section.ts — V2-0 (spec V2 §4.0, plan
// 20261006-1008 phase-01).
//
// Mục prompt "Quyết định đã chốt cho ticket này": câu trả lời của NGƯỜI tới
// được worker. Đo trên 69643b2 (= 3.34.2): `answer` chỉ ghi interview.json và
// 0 chỗ prompt/dispatch đọc `interview.answers` — worker chạy lại nhận prompt
// byte-identical với lần trước khi hỏi, nên dễ hỏi lại hoặc tự chọn khác.
//
// Đọc ĐỒNG BỘ mỗi lần dựng prompt (khác playbook đọc một lần lúc khởi tạo):
// câu trả lời đến giữa `run` (drain ở tick boundary) phải vào ngay prompt của
// lần dispatch kế tiếp, và `buildPrompt` của supervisor-loop là hàm sync.
//
// Chỉ in câu của ĐÚNG ticket (so khớp chính xác, T-1 ≠ T-10) và chỉ khi có câu
// trả lời — ticket chưa có answer ⇒ không in gì, prompt = baseline (I1 E1).
import { readFileSync } from "node:fs";
import { join } from "node:path";
import { CHAT_DELEGATED_LABEL, isChatDelegatedActor } from "./chat-delegated-attribution.js";
import { optionLabelForId } from "./interview-answer-option-id-resolution.js";
import { INTERVIEW_FILE_NAME } from "./interview-store.js";
import { isNeedsDecisionQuestionOfTicket } from "./needs-decision-question-ticket-match.js";
import {
  type InterviewAnswer,
  type InterviewQuestion,
  InterviewSchema,
} from "./schemas/interview-schema.js";

/** Tiêu đề cố định — golden I1 lọc đúng mục này (E1), đổi chữ là đổi hợp đồng. */
export const DECIDED_QUESTIONS_SECTION_TITLE = "## Quyết định đã chốt cho ticket này";

export interface DecidedQuestion {
  question: InterviewQuestion;
  answer: InterviewAnswer;
}

/**
 * Câu đã trả lời của `ticketId`. Thiếu/hỏng interview.json ⇒ `[]` — cùng
 * fail-open với `readInterview`: không có file là trạng thái bình thường của
 * hầu hết mission, và file hỏng thì gate cũng đã không chặn được gì.
 */
export function readDecidedQuestionsForTicket(
  controlRoot: string,
  ticketId: string,
): DecidedQuestion[] {
  let raw: unknown;
  try {
    raw = JSON.parse(readFileSync(join(controlRoot, INTERVIEW_FILE_NAME), "utf8"));
  } catch {
    return [];
  }
  const parsed = InterviewSchema.safeParse(raw);
  if (!parsed.success) return [];
  const { questions, answers } = parsed.data;
  return questions.flatMap((question) => {
    const answer = answers[question.id];
    return answer !== undefined && isNeedsDecisionQuestionOfTicket(question, ticketId)
      ? [{ question, answer }]
      : [];
  });
}

/**
 * Một dòng prompt, không phá được cấu trúc mục: `JSON.stringify` thoát xuống
 * dòng/ngoặc, và U+2028/U+2029 (JSON để nguyên) cũng được thoát.
 */
function quoted(value: unknown): string {
  return JSON.stringify(value)
    .replace(/\u2028/g, "\\u2028")
    .replace(/\u2029/g, "\\u2029");
}

/**
 * Chuỗi người nhập đi qua `quoted`: xuống dòng/`## ` trong câu trả lời không
 * phá được cấu trúc mục của prompt. Giá trị `single`/`multiple` vốn đã bị
 * `recordInterviewAnswer` ràng buộc vào options của câu hỏi.
 */
export function renderDecidedQuestionsSection(decided: DecidedQuestion[]): string[] {
  if (decided.length === 0) return [];
  const lines = [
    "",
    DECIDED_QUESTIONS_SECTION_TITLE,
    "- Người đã trả lời các câu dưới đây — làm theo đúng lựa chọn, KHÔNG hỏi lại. Trả `needs_decision` lặp đúng câu đã chốt ⇒ contract_error (tính một lần thất bại); chỉ hỏi câu MỚI khi gặp điểm cần quyết khác.",
  ];
  for (const { question, answer } of decided) {
    lines.push(`- question_id: ${question.id}`);
    lines.push(`  - câu hỏi: ${quoted(question.title)}`);
    lines.push(choiceLine(question, answer));
    lines.push(`  - nguồn: ${sourceOf(answer.answered_by.replace(/[\r\n\u2028\u2029]/g, " "))}`);
    lines.push(`  - thời điểm: ${answer.answered_at}`);
  }
  return lines;
}

/** V2-0: câu v1 lưu NHÃN. V2-1 (E3): câu có `option_ids` lưu id ⇒ in id + nhãn. */
function choiceLine(question: InterviewQuestion, answer: InterviewAnswer): string {
  if (question.type === "text") return `  - giá trị người nhập: ${quoted(answer.value)}`;
  if (question.option_ids !== undefined && typeof answer.value === "string") {
    const pairs = question.option_ids.map((id) => ({ id, label: optionLabelForId(question, id) }));
    const label = optionLabelForId(question, answer.value);
    return `  - lựa chọn đã chốt: option id ${quoted(answer.value)} (nhãn ${quoted(label ?? "")}) — các lựa chọn đã hỏi: ${quoted(pairs)}`;
  }
  return `  - lựa chọn đã chốt: ${quoted(answer.value)} — các lựa chọn đã hỏi: ${quoted(question.options ?? [])}`;
}

/**
 * I6: `delegated:<G0 id>` (máy tự áp), `redirect:<directive>` (06i R1) in nguyên.
 * `chat_delegated:<os>` (06k M2) in NHÃN — worker chép nguồn vào claim mà G0 soi
 * `/delegat/` sẽ bắt nhầm. Không bao giờ như người chọn.
 */
function sourceOf(answeredBy: string): string {
  if (isChatDelegatedActor(answeredBy)) return CHAT_DELEGATED_LABEL;
  return /^(delegated|redirect):/.test(answeredBy) ? answeredBy : `user:${answeredBy}`;
}

/**
 * Dòng cho builder: rỗng khi không có `controlRoot` (caller không nối interview).
 * Mọi phiên — kể cả review context sạch (T-9002, review-clean-v1) — nhận câu đã
 * trả lời của ĐÚNG ticket đang chạy (Codex review 261006 F1: reviewer từng bị
 * phạt contract_error khi hỏi lặp câu mà nó chưa từng thấy câu trả lời). Mục
 * này vẫn lọc theo ticket và không đọc review.md, nên phiên sạch KHÔNG thấy câu
 * hỏi của ticket khác. C2 sau 06f R4: lựa chọn đã chốt của ticket khác tới hai
 * phiên review qua mục mission ở supervisor-loop.ts — chỉ lựa chọn, không câu hỏi.
 */
export function decidedQuestionsPromptLines(
  controlRoot: string | undefined,
  ticketId: string,
): string[] {
  if (controlRoot === undefined) return [];
  return renderDecidedQuestionsSection(readDecidedQuestionsForTicket(controlRoot, ticketId));
}
